import { TONE_BADGE, TONE_TEXT } from './statusColors';

/**
 * O ESTADO DE UM DESTINATÁRIO NO ENVIO DA FATURA (pedido do Heitor de 04/11/2026, regra aprovada pelo Maestro). Uma
 * regra só para a trilha, os destinatários do comprovante e o selo da lista. Antes, o selo saía do `status` do log e o
 * texto de devolução saía do `bounceReason`, sem conversar: "Aberto" com um 550 em vermelho embaixo, "Entregue" com
 * "Unable to parse reason from bounce report", e 34 recusas definitivas (550/553/554) mostradas como "Enviado", porque
 * o webhook gravou `bouncedAt` e o motivo mas deixou o status em `processed`.
 *
 * Por destinatário, no ÚLTIMO envio, nesta ordem (a primeira que casa vale):
 *  1. abertura registrada → Aberto. Alguém leu: nunca é falha. Devolução junto vira linha de AVISO, e diz quando ela cita
 *     outro endereço (o encaminhamento interno do cliente, caso ti.infra@ de 04/11);
 *  2. status de falha, ou recusa 5xx sem entrega → Falha. Só aqui o texto é vermelho;
 *  3. recusa 4xx (temporária) sem entrega → Adiado, sem entrega (aviso);
 *  4. entregue, com devolução DEPOIS da entrega → Entregue, com devolução (aviso; caso faturas1@ de 04/11);
 *  5. entregue → Entregue; 6. o resto → Enviado (aguardando o servidor).
 * O texto cru do servidor fica no título, inteiro.
 */
export interface LogDoEnvio {
  status?: string | null;
  recipientEmail?: string | null;
  deliveredAt?: string | null;
  openedAt?: string | null;
  bouncedAt?: string | null;
  bounceReason?: string | null;
}

export type EstadoDoEnvio = 'aberto' | 'falha' | 'adiado' | 'entregue_com_devolucao' | 'entregue' | 'enviado';

/** Onde o estado conta no resumo da fatura: o aviso não é entrega, e a linha vira "e-mail parcial". */
export type ContaComo = 'ok' | 'falha' | 'aviso' | 'aguardando';

export interface Devolucao {
  tom: 'perigo' | 'aviso';
  texto: string;
  cru: string;
}

export interface LeituraDoEnvio {
  estado: EstadoDoEnvio;
  rotulo: string;
  icone: string;
  /** O selo, do mapa de tons (statusColors): quieto, sucesso, aviso, falha. */
  selo: string;
  contaComo: ContaComo;
  devolucao: Devolucao | null;
}

const STATUS_DE_FALHA = ['bounced', 'dropped', 'spam_report', 'failed'];

const ROTULO: Record<EstadoDoEnvio, { rotulo: string; icone: string; selo: string; contaComo: ContaComo }> = {
  aberto: { rotulo: 'Aberto', icone: '✓', selo: TONE_BADGE.success, contaComo: 'ok' },
  falha: { rotulo: 'Falha', icone: '✕', selo: TONE_BADGE.danger, contaComo: 'falha' },
  adiado: { rotulo: 'Adiado, sem entrega', icone: '⚠', selo: TONE_BADGE.warning, contaComo: 'aviso' },
  entregue_com_devolucao: { rotulo: 'Entregue, com devolução', icone: '⚠', selo: TONE_BADGE.warning, contaComo: 'aviso' },
  entregue: { rotulo: 'Entregue', icone: '✓', selo: TONE_BADGE.neutral, contaComo: 'ok' },
  enviado: { rotulo: 'Enviado', icone: '', selo: TONE_BADGE.neutral, contaComo: 'aguardando' },
};

/** A cor do texto da devolução: vermelho só com selo de falha. */
export const COR_DA_DEVOLUCAO: Record<Devolucao['tom'], string> = { perigo: TONE_TEXT.danger, aviso: TONE_TEXT.warning };

const codigoSmtp = (motivo: string): number | null => {
  const m = /^\s*([2-5])\d\d\b/.exec(motivo);
  return m ? Number(m[1]) : null;
};

/** Endereços citados no motivo que não são o destinatário: a devolução de um encaminhamento do lado do cliente. */
export function outroEnderecoCitado(motivo: string, destinatario?: string | null): boolean {
  const citados: string[] = Array.from(motivo.match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g) || []);
  const dest = String(destinatario || '').toLowerCase();
  return citados.some((e) => e.toLowerCase() !== dest);
}

const SEM_MOTIVO = /unable to parse reason/i;

function textoDaDevolucao(estado: EstadoDoEnvio, motivo: string, log: LogDoEnvio): string {
  const legivel = SEM_MOTIVO.test(motivo) ? 'O servidor do cliente devolveu um aviso sem motivo legível' : motivo;
  if (estado === 'aberto') {
    return outroEnderecoCitado(motivo, log.recipientEmail)
      ? 'Devolução depois da entrega: a mensagem cita outro endereço do cliente, provável encaminhamento interno dele. A fatura chegou e foi aberta.'
      : `Devolução registrada, mas a fatura foi aberta: ${legivel}`;
  }
  if (estado === 'entregue_com_devolucao') {
    return `${legivel}, depois de aceitar a entrega. Não dá para saber se chegou à caixa.`;
  }
  return legivel;
}

export function lerEnvio(log: LogDoEnvio): LeituraDoEnvio {
  const st = String(log.status || '');
  const motivo = String(log.bounceReason || '').trim();
  const devolveu = !!log.bouncedAt || STATUS_DE_FALHA.includes(st);
  const entregue = !!log.deliveredAt || ['delivered', 'opened', 'clicked'].includes(st);
  const codigo = motivo ? codigoSmtp(motivo) : null;

  let estado: EstadoDoEnvio;
  if (log.openedAt || st === 'opened' || st === 'clicked') estado = 'aberto';
  else if (STATUS_DE_FALHA.includes(st) || (devolveu && !entregue && codigo !== 4)) estado = 'falha';
  else if (devolveu && !entregue && codigo === 4) estado = 'adiado';
  else if (devolveu && entregue) estado = 'entregue_com_devolucao';
  else if (entregue) estado = 'entregue';
  else estado = 'enviado';

  const devolucao: Devolucao | null = motivo && (devolveu || estado !== 'enviado')
    ? { tom: estado === 'falha' ? 'perigo' : 'aviso', texto: textoDaDevolucao(estado, motivo, log), cru: motivo }
    : null;
  return { estado, ...ROTULO[estado], devolucao };
}
